import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { PersonalDetails, AddressFields, AddressDetails, ContactDetails, EmploymentDetails, BankingDetails, EmployeeAddData } from '../types/EmployeeAddContext.types';

// Re-export types for backward compatibility
export type { PersonalDetails, AddressFields, AddressDetails, ContactDetails, EmploymentDetails, BankingDetails, EmployeeAddData };

type EmployeeAddContextValue = {
  data: EmployeeAddData;
  setPersonalDetails: (next: PersonalDetails) => void;
  setAddressDetails: (next: AddressDetails) => void;
  setContactDetails: (next: ContactDetails) => void;
  setEmploymentDetails: (next: EmploymentDetails) => void;
  setBankingDetails: (next: BankingDetails) => void;
  setAllEmployeeData: (next: EmployeeAddData) => void;
  reset: () => void;
};

const STORAGE_KEY = 'pis_employee_add';

const defaultAddressFields: AddressFields = {
  streetAddress: '',
  city: '',
  province: '',
  postalCode: '',
  country: 'South Africa',
};

const defaultData: EmployeeAddData = {
  personalDetails: {
    employeeCode: '',
    title: '',
    idNumber: '',
    firstName: '',
    middleName: '',
    lastName: '',
    initials: '',
    dateOfBirth: '',
    passportNumber: '',
    passportCountry: '',
    department: '',
    departmentId: '',
    role: '',
    roleId: '',
    employeeType: '',
    seekingAsylum: false,
    refugee: false,
    relatedToSupervisor: '',
    isDomesticWorker: false,
    isLabourBroker: false,
    isIndependentContractor: false
  },
  addressDetails: {
    physicalAddress: { ...defaultAddressFields },
    postalAddress: { ...defaultAddressFields },
    sameAsPhysical: false
  },
  contactDetails: {
    cellphoneNumber: '',
    emailAddress: '',
    emergencyCellphoneNumber: '',
    emergencyEmailAddress: ''
  },
  employmentDetails: {
    employeeStartDate: '',
    payCycle: '',
    paycycleTypeId: null,
    taxTable: '',
    irp5StartDate: '',
    incomeTaxNumber: ''
  },
  bankingDetails: {
    accountNumber: '',
    accountType: '',
    branchName: '',
    branchCode: ''
  }
};

export function createDefaultEmployeeAddData(): EmployeeAddData {
  return JSON.parse(JSON.stringify(defaultData)) as EmployeeAddData;
}

function applyEmployeeDefaults(next?: Partial<EmployeeAddData>): EmployeeAddData {
  const defaults = createDefaultEmployeeAddData();
  if (!next) {
    return defaults;
  }

  const mergeAddress = (incoming?: Partial<AddressDetails>): AddressDetails => ({
    physicalAddress: {
      ...defaults.addressDetails.physicalAddress,
      ...(incoming?.physicalAddress ?? {})
    },
    postalAddress: {
      ...defaults.addressDetails.postalAddress,
      ...(incoming?.postalAddress ?? {})
    },
    sameAsPhysical: incoming?.sameAsPhysical ?? defaults.addressDetails.sameAsPhysical
  });

  return {
    personalDetails: {
      ...defaults.personalDetails,
      ...(next.personalDetails ?? {})
    },
    addressDetails: mergeAddress(next.addressDetails),
    contactDetails: {
      ...defaults.contactDetails,
      ...(next.contactDetails ?? {})
    },
    employmentDetails: {
      ...defaults.employmentDetails,
      ...(next.employmentDetails ?? {})
    },
    bankingDetails: {
      ...defaults.bankingDetails,
      ...(next.bankingDetails ?? {})
    }
  };
}

export const EmployeeAddContext = createContext<EmployeeAddContextValue | null>(null);

export function clearEmployeeAddDraft() {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
}

function safeParseStored(raw: string | null): EmployeeAddData | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as EmployeeAddData;
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export type EmployeeAddProviderProps = {
  children: React.ReactNode;
  initialData?: EmployeeAddData;
  persist?: boolean;
};

export function EmployeeAddProvider({ children, initialData, persist = true }: EmployeeAddProviderProps) {
  const [data, setData] = useState<EmployeeAddData>(() => {
    if (initialData) {
      return applyEmployeeDefaults(initialData);
    }
    if (persist) {
      const stored = safeParseStored(localStorage.getItem(STORAGE_KEY));
      if (stored) {
        return applyEmployeeDefaults(stored);
      }
    }
    return createDefaultEmployeeAddData();
  });

  useEffect(() => {
    if (!persist) return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data, persist]);

  const setPersonalDetails = useCallback((next: PersonalDetails) => {
    setData((prev) => ({ ...prev, personalDetails: next }));
  }, []);

  const setAddressDetails = useCallback((next: AddressDetails) => {
    setData((prev) => ({ ...prev, addressDetails: next }));
  }, []);

  const setContactDetails = useCallback((next: ContactDetails) => {
    setData((prev) => ({ ...prev, contactDetails: next }));
  }, []);

  const setEmploymentDetails = useCallback((next: EmploymentDetails) => {
    setData((prev) => ({ ...prev, employmentDetails: next }));
  }, []);

  const setBankingDetails = useCallback((next: BankingDetails) => {
    setData((prev) => ({ ...prev, bankingDetails: next }));
  }, []);

  const setAllEmployeeData = useCallback((next: EmployeeAddData) => {
    setData(applyEmployeeDefaults(next));
  }, []);

  const reset = useCallback(() => {
    setData(createDefaultEmployeeAddData());
  }, []);

  const value: EmployeeAddContextValue = {
    data,
    setPersonalDetails,
    setAddressDetails,
    setContactDetails,
    setEmploymentDetails,
    setBankingDetails,
    setAllEmployeeData,
    reset
  };

  return (
    <EmployeeAddContext.Provider value={value}>
      {children}
    </EmployeeAddContext.Provider>
  );
}

export function useEmployeeAdd() {
  const context = useContext(EmployeeAddContext);
  if (!context) {
    throw new Error('useEmployeeAdd must be used within EmployeeAddProvider');
  }
  return context;
}

 export function useOptionalEmployeeAdd() {
   return useContext(EmployeeAddContext);
 }
